<template>
  <div>
    <Button
      variant="text"
      icon="pi pi-pencil"
      size="small"
      @click="toggleOpen(props.noteId)"
    />

    <!-- Edit Note Dialog -->
    <Dialog
      v-model:visible="visible"
      header="Edit Note"
      :style="{ width: '600px' }"
      :modal="true"
    >
      <div
        v-if="isLoadingNote"
        class="flex justify-center items-center min-h-[200px]"
      >
        <ProgressSpinner />
      </div>

      <div
        v-else
        class="flex flex-col gap-4"
      >
        <Tabs value="0">
          <TabList>
            <Tab value="0">
              <div class="flex items-center gap-1">
                <i class="pi pi-pen-to-square" />
                Note
              </div>
            </Tab>
            <Tab value="1">
              <div class="flex items-center gap-1">
                <i class="pi pi-paperclip" />
                Attachments
              </div>
            </Tab>
            <Tab
              value="2"
            >
              <div class="flex items-center gap-1">
                <i class="pi pi-share-alt" />
                Associations
              </div>
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel value="0">
              <div class="flex flex-col gap-4 h-80">
                <div class="flex flex-col gap-2">
                  <label for="type">
                    Type
                    <span class="text-red-500">*</span>
                  </label>

                  <Dropdown
                    id="type"
                    v-model="noteData.type"
                    :options="typeOptions"
                    optionLabel="name"
                    optionValue="value"
                    placeholder="Select type"
                    class="w-full"
                  />
                </div>

                <div class="flex flex-1 flex-col gap-2">
                  <label for="content">
                    Content
                    <span class="text-red-500">*</span>
                  </label>

                  <WidgetsRichTextEditor
                    v-model:data="noteData.content"
                    class="!h-[100px]"
                    :isEditing="true"
                  />
                </div>
              </div>
            </TabPanel>

            <TabPanel value="1">
              <div class="h-80">
                <UploaderPublicStorage
                  ref="uploaderRef"
                  v-model:selectedFiles="stagedFiles"
                  v-model:uploadedFiles="uploadedFiles"
                  v-model:isSubmitting="isSubmitting"
                  v-model:filesPath="filesPath"
                />

                <div v-if="noteData.attachments?.length > 0">
                  <Divider />
                  <span>Files Attached:</span>
                  <div class="flex flex-col ">
                    <div
                      v-for="(item, index) in noteData.attachments"
                      :key="index"
                    >
                      <div class="flex flex-1 justify-between">
                        <div class="flex gap-2 items-center">
                          <i
                            class="pi pi-paperclip text-blue-500"
                            style="font-size: 0.9rem"
                          />
                          <a
                            :href="item.publicUrl"
                            class="hover:cursor-pointer text-blue-500"
                            target="_blank"
                          >
                            {{ item.name }}
                          </a>
                        </div>

                        <div class="flex shrink-0">
                          <Button
                            icon="pi pi-pencil"
                            size="small"
                            variant="text"
                            @click="showEditAttachment(item)"
                          />

                          <Button
                            severity="danger"
                            icon="pi pi-trash"
                            size="small"
                            variant="text"
                            @click="removeAttachment(item.dropboxFileId)"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabPanel>

            <TabPanel value="2">
              <div class="flex flex-col gap-4 h-80">
                <div
                  class="flex flex-col gap-2"
                >
                  <label for="type">
                    Contact
                    <span class="text-xs">[optional]</span>
                  </label>

                  <ContactSelectSingle
                    v-model:selectedContact="selectedContact"
                    :showCreateContact="false"
                    :isDisabled="noteData?.contactId ? true : false"
                  />
                </div>

                <div
                  class="flex flex-col gap-2"
                >
                  <label for="type">
                    Business
                    <span class="text-xs">[optional]</span>
                  </label>

                  <Select
                    v-model="selectedBusiness"
                    class="w-full"
                    :options="businesses"
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Select business"
                    filter
                    filterPlaceholder="Search businesses"
                    :disabled="noteData.businessId"
                  />
                </div>
              </div>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>

      <template #footer>
        <div
          class="w-full flex items-center px-5"
          :class="isLoadingNote ? 'justify-end' : 'justify-between'"
        >
          <div
            v-if="!isLoadingNote"
            class="flex gap-2 items-center"
          >
            <Checkbox
              v-model="noteData.showOnVendorReport"
              binary
            />

            <label for="showOnVendorReport">
              Show Note On Vendor Report
            </label>
          </div>

          <div class="flex gap-2 items-center">
            <Button
              outlined
              label="Cancel"
              icon="pi pi-times"
              @click="hideDialog"
            />
            <Button
              label="Save"
              icon="pi pi-check"
              :loading="loading"
              @click="saveNote"
            />
          </div>
        </div>
      </template>
    </Dialog>

    <!-- Rename Attachment Dialog -->
    <Dialog
      v-model:visible="showEditModal"
      modal
      header="Rename file"
      :style="{ width: '25rem' }"
    >
      <div class="flex items-center gap-4 mb-4">
        <InputText
          v-model="editForm.name"
          placeholder="New File Name"
          class="flex-auto"
          autocomplete="off"
          fluid
        />
      </div>

      <div class="flex justify-end gap-2">
        <Button
          outlined
          label="Cancel"
          icon="pi pi-times"
          @click="showEditModal = false"
        />
        <Button
          label="Save"
          icon="pi pi-check"
          :loading="isLoading"
          :disabled="isLoading"
          @click="updateAttachmentName(editForm.dropboxFileId)"
        />
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import type { Business } from '@prisma/client'

const props = defineProps({
  noteId: String,
})
const emit = defineEmits(['refresh'])

const { updateNote, getNoteById, updateNoteAttachments } = useNotes()
const toast = useToast()
const route = useRoute()

// Edit Note Dialog
const visible = ref(false)

const toggleOpen = async (noteId: string) => {
  visible.value = true
  await loadNote(noteId)
}

const hideDialog = () => {
  visible.value = false
  submitted.value = false
  resetForm()
}

// Rename Attachment Dialog
const showEditModal = ref(false)
const isLoading = ref(false)

const editForm = ref()

const showEditAttachment = (item) => {
  editForm.value = JSON.parse(JSON.stringify(item))
  showEditModal.value = true
}

const updateAttachmentName = async (attachmentFileId: string) => {
  if (!editForm.value.name?.trim()) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Invalid name',
      life: 3000,
    })
    return
  }

  isLoading.value = true

  const prevAttachments = [...noteData.value.attachments]

  const updatedAttachments = prevAttachments.map(item =>
    item.dropboxFileId === attachmentFileId ? { ...item, name: editForm.value.name.trim() } : item,
  )

  if (JSON.stringify(updatedAttachments) === JSON.stringify(prevAttachments)) {
    isLoading.value = false
    showEditModal.value = false
    return
  }

  await updateNoteAttachments(props.noteId, updatedAttachments)

  noteData.value.attachments = updatedAttachments

  toast.add({
    severity: 'success',
    summary: 'Success',
    detail: 'File name updated successfully',
    life: 3000,
  })

  showEditModal.value = false
  isLoading.value = false
}

const removeAttachment = async (attachmentFileId: string) => {
  if (confirm('Are you sure?')) {
    isLoading.value = true

    const prevAttachments = [...noteData.value.attachments]

    const updatedAttachments = prevAttachments.filter(
      item => item.dropboxFileId !== attachmentFileId,
    )

    if (updatedAttachments.length === prevAttachments.length) {
      isLoading.value = false
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to remove attachment',
        life: 3000,
      })
      return
    }

    await updateNoteAttachments(props.noteId, updatedAttachments)

    noteData.value.attachments = updatedAttachments

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'File removed successfully',
      life: 3000,
    })

    showEditModal.value = false
    isLoading.value = false
  }
}

// Form
const loading = ref(false)
const submitted = ref(false)

const noteData = ref({
  type: '',
  content: '',
  showOnVendorReport: true,
})

const errors = ref({
  type: false,
  content: false,
})

const resetForm = () => {
  noteData.value = {
    type: '',
    content: '',
    showOnVendorReport: true,
  }
  errors.value = {
    type: false,
    content: false,
  }
}

const typeOptions = ref([
  { name: 'Note', value: 'Note' },
  { name: 'Call', value: 'Call' },
  { name: 'Email', value: 'Email' },
  { name: 'SMS', value: 'SMS' },
  { name: 'Inspection', value: 'Inspection' },
  { name: 'Offer', value: 'Offer' },
  { name: 'Meeting', value: 'Meeting' },
])

const validateForm = (): boolean => {
  errors.value.type = !noteData.value.type

  if (
    noteData.value.content === '<p></p>'
    || noteData.value.content === '<p> </p>'
    || noteData.value.content === ''
    || noteData.value.content === '<p style="margin-left: 0px!important;"></p>'
  ) {
    errors.value.content = true
  }
  else {
    errors.value.content = false
  }

  return !Object.values(errors.value).some(Boolean)
}

// Files Uploader
const filesPath = ref('notes')
const stagedFiles = ref([])
const uploaderRef = ref(null)
const isSubmitting = ref(false)
const uploadedFiles = ref<JSON[]>([])

const uploadAttachments = async () => {
  await uploaderRef.value?.uploadFiles()
}

// Associations
const { fetchContactLinkedBusinesses } = useContacts()
const { getAllPageAccessByBusinessId } = usePageAccess()
const contacts = ref()
const selectedContact = ref()

const { getAllBusinesses } = useBusiness()
const businesses = ref()
const selectedBusiness = ref()

// Load Note
const isLoadingNote = ref(false)

const loadNote = async (noteId: string) => {
  isLoadingNote.value = true

  try {
    const { data, error } = await getNoteById(noteId)

    if (error) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch note',
        life: 3000,
      })
      return
    }

    noteData.value = data
    selectedContact.value = data?.contactId
    selectedBusiness.value = data?.businessId

    // Load associations
    if (route.path.startsWith('/contacts/') && selectedContact.value) {
      const resLinkedBusinesses = await fetchContactLinkedBusinesses(selectedContact.value)
      businesses.value = resLinkedBusinesses.map((business: {
        Business: Business
      }) => {
        return {
          id: business.Business.id,
          name: business.Business.name,
        }
      })
    }
    else {
      const resBusinesses = await getAllBusinesses()
      businesses.value = resBusinesses
    }

    if (route.path.startsWith('/businesses/') && selectedBusiness.value) {
      const resPageAccess = await getAllPageAccessByBusinessId(selectedBusiness.value)
      contacts.value = resPageAccess.data?.filter(item => item.contactId).map((record) => {
        return {
          id: record.contactId,
          name: record.firstName + ' ' + record.lastName,
        }
      })
    }
  }
  catch (error) {
    console.error('Error fetching note:', error)
  }
  finally {
    isLoadingNote.value = false
  }
}

// Save Note
const saveNote = async () => {
  submitted.value = true

  const isValid = validateForm()

  if (errors.value.type && submitted) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Type is required',
      life: 3000,
    })
  }

  if (errors.value.content && submitted) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Content is required',
      life: 3000,
    })
  }

  if (!isValid) return

  loading.value = true

  try {
    const contactId = selectedContact.value
    const businessId = selectedBusiness.value

    const prevAttachments = [...noteData.value.attachments]

    const noteAttachments = ref<JSON[]>([])

    await uploadAttachments()

    // Check if any files were uploaded
    if (uploadedFiles.value.length > 0) {
      noteAttachments.value = [...prevAttachments, ...uploadedFiles.value]

      // Update note with new attachments
      const { data, error } = await updateNote(props.noteId, {
        type: noteData.value.type,
        content: noteData.value.content,
        showOnVendorReport: noteData.value.showOnVendorReport,
        attachments: noteAttachments.value,
      },
      contactId,
      businessId,
      )

      if (error) {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update note',
          life: 3000,
        })
        return
      }
    }
    else {
      // Update note content and keep previous attachments
      const { data, error } = await updateNote(props.noteId, {
        type: noteData.value.type,
        content: noteData.value.content,
        showOnVendorReport: noteData.value.showOnVendorReport,
      },
      contactId,
      businessId,
      )

      if (error) {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update note',
          life: 3000,
        })
        return
      }
    }

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Note updated successfully',
      life: 3000,
    })

    hideDialog()
    emit('refresh')
  }
  catch (error) {
    console.error('Error updating note:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update note',
      life: 3000,
    })
  }
  finally {
    loading.value = false
  }
}
</script>
